import { DialogActions, Stack } from '@mui/material';
import config from 'config';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
	clearTemporaryAttachments as clearTemporaryAttachmentsAction,
	getPromptsRemainingLimit as getPromptsRemainingLimitAction,
} from '../../../../actions/aiAssistant.actions';
import { AsyncActionStatus } from '../../../../actions/common.actionTypes';
import { spaces } from '../../../../assets/styles/themes/tokens';
import {
	areTemporaryAttachmentsReady,
	getCurrentConversation,
	getGeneratingPodcastIdForCurrentConversation,
	getTemporaryAttachments,
} from '../../../../reducers/aiAssistant.selectors';
import {
	AIAssistantDisplayMode,
	aiAssistantContext,
} from '../../../../state/AIAssistantContextProvider';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import useAction from '../../../../utils/useAction';
import useIsPodcastGenerating from '../../../../utils/useIsPodcastGenerating';
import useScreenSize from '../../../../utils/useScreenSize';
import useIsPodcastLoadingViewActive from '../Chat/useIsPodcastLoadingViewActive';
import AttachmentsButton from './PromptInput/AttachmentsButton';
import PromptInput from './PromptInput/PromptInput';
import SendButton from './PromptInput/SendButton';
import StopButton from './PromptInput/StopButton';

export const AI_USER_PROMPT_CHAR_LIMIT = config.ai.readerChat.maxUserPromptLength;
export const DEFAULT_DIALOG_FOOTER_HEIGHT = 200;
export const DEFAULT_PAGE_FOOTER_HEIGHT = 184;
const AIAssistantFooter = ({ caption }: { caption?: React.ReactNode }) => {
	const { t } = useTranslation();
	const {
		dialogFooterRef,
		isUserPromptLimitReached,
		isPromptLimitReached,
		showPromptLimitAlert,
		processNewConversation,
		stopGeneration,
		isStoppingResponse,
		continueConversation,
		displayMode,
	} = useContext(aiAssistantContext);
	const { isTextEditorOpen } = useContext(aiAssistantEditorContext);
	const isDialogMode = displayMode === AIAssistantDisplayMode.DIALOG;
	const temporaryAttachments = useSelector(getTemporaryAttachments);
	const attachmentsReady = useSelector(areTemporaryAttachmentsReady);
	const clearTemporaryAttachments = useAction(clearTemporaryAttachmentsAction);
	const currentConversation = useSelector(getCurrentConversation);
	const isLoadingResponse = currentConversation?.status === AsyncActionStatus.LOADING;
	const [userPrompt, setUserPrompt] = useState('');
	const { isXsScreen, isSmScreen } = useScreenSize();
	const isXsOrSmScreen = isXsScreen || isSmScreen;
	const formRef = useRef<HTMLFormElement>(null);
	// State-based generating signal: holds in the chat view for podcasts generated
	// or refined mid-chat, where the view-based hook below is false.
	const generatingPodcastId = useSelector(getGeneratingPodcastIdForCurrentConversation);
	// View-based fallback for the creation flow's first seconds, before the poll
	// has written the podcast (and its id) into the store.
	const isCreationViewGenerating = useIsPodcastGenerating();
	const isPodcastGenerating = !!generatingPodcastId || isCreationViewGenerating;
	// Layout signal, not a generation signal: true while the podcast placeholder
	// occupies the left pane, so the padding matches AIAssistantChat's split mode.
	const isPodcastLoadingViewActive = useIsPodcastLoadingViewActive();
	// Blocked mid-upload: `temporaryAttachments` still holds the client-side
	// placeholder id, which resolves to nothing server-side.
	const isSubmissionDisabled =
		isUserPromptLimitReached ||
		isPromptLimitReached ||
		isPodcastGenerating ||
		!attachmentsReady ||
		userPrompt.trim().length === 0;

	const handleUserPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let value = event.target.value;
		if (value.length > AI_USER_PROMPT_CHAR_LIMIT) {
			value = value.slice(0, AI_USER_PROMPT_CHAR_LIMIT);
		}
		setUserPrompt(value);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (isLoadingResponse || isSubmissionDisabled) {
			showPromptLimitAlert();
			return;
		}

		const conversationData = {
			message: userPrompt,
			...(temporaryAttachments.length > 0
				? { messageContext: { attachments: temporaryAttachments } }
				: {}),
		};

		if (currentConversation) {
			continueConversation(conversationData);
		} else {
			processNewConversation(conversationData);
		}

		setUserPrompt('');
		clearTemporaryAttachments();
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			formRef.current?.requestSubmit();
		}
	};

	const handleStopButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		// One entry point for both cases; the provider routes on what exists
		// (a running stream turn, or an already-created podcast).
		stopGeneration();
	};

	const fetchPromptsRemainingLimit = useAction(getPromptsRemainingLimitAction);
	const handleDisabledSendClick = () => {
		if (isSubmissionDisabled && isPromptLimitReached) {
			fetchPromptsRemainingLimit();
			showPromptLimitAlert();
		}
	};

	// The combined predicate includes the creation-flow view fallback, so Stop
	// is available in the gap before the poll has stored the podcast record;
	// stopGeneration works without an id (conversation-level stop + sweep).
	const rightItems =
		isLoadingResponse || isPodcastGenerating ? (
			<StopButton onStopResponse={handleStopButtonClick} disabled={isStoppingResponse} />
		) : (
			// biome-ignore lint/a11y/noStaticElementInteractions: click-catcher over the disabled send button; the button itself handles real interaction
			// biome-ignore lint/a11y/useKeyWithClickEvents: same click-catcher; keyboard users interact with the button, not the wrapper
			<span onClick={handleDisabledSendClick}>
				<SendButton disabled={isSubmissionDisabled} />
			</span>
		);

	return (
		<DialogActions
			ref={dialogFooterRef}
			data-testid="ai-assistant-footer"
			sx={{
				padding:
					isDialogMode || isXsOrSmScreen || isTextEditorOpen || isPodcastLoadingViewActive
						? spaces.md
						: spaces.xxl,
				minHeight: isDialogMode ? DEFAULT_DIALOG_FOOTER_HEIGHT : DEFAULT_PAGE_FOOTER_HEIGHT,
				maxWidth: '1440px',
				margin: '0 auto',
				width: '100%',
			}}
		>
			<Stack gap={spaces.md} sx={{ width: '100%' }}>
				<form ref={formRef} onSubmit={handleSubmit}>
					<PromptInput
						onChange={handleUserPromptChange}
						onKeyDown={handleKeyDown}
						promptValue={userPrompt}
						maxPromptLength={AI_USER_PROMPT_CHAR_LIMIT}
						placeholder={t('Enter prompt')}
						rightItems={rightItems}
						leftItems={<AttachmentsButton />}
					/>
				</form>
				{caption && caption}
			</Stack>
		</DialogActions>
	);
};

export default AIAssistantFooter;
